import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Drawer,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AddWorkoutDetails from "../AddWorkoutDetails";
import Loading from "../Layout/Loading";
import { useApi } from "../../utils/api";
import { useAuth } from "../../utils/auth";
import { Autocomplete } from "@mui/material";

const ProgramModal = ({ open, onClose, templates, initialProgram, onSave }) => {
  const [name, setName] = useState(initialProgram?.name || "");
  const [lengthWeeks, setLengthWeeks] = useState(
    initialProgram?.lengthWeeks || 1
  );
  const [weeks, setWeeks] = useState(() => {
    if (initialProgram?.weeks?.length) return initialProgram.weeks;
    return Array.from({ length: lengthWeeks }, () => []);
  });

  // Rehydrate fields when editing a different program or reopening
  useEffect(() => {
    if (initialProgram) {
      setName(initialProgram.name || "");
      setLengthWeeks(
        initialProgram.lengthWeeks || initialProgram.weeks?.length || 1
      );
      setWeeks(
        initialProgram.weeks?.length
          ? initialProgram.weeks
          : Array.from({ length: initialProgram.lengthWeeks || 1 }, () => [])
      );
    } else {
      setName("");
      setLengthWeeks(1);
      setWeeks(Array.from({ length: 1 }, () => []));
    }
  }, [initialProgram, open]);

  // Keep weeks array in sync with length
  useEffect(() => {
    setWeeks((prev) => {
      const next = [...prev];
      if (lengthWeeks > prev.length) {
        for (let i = prev.length; i < lengthWeeks; i += 1) {
          next.push([]);
        }
      } else if (lengthWeeks < prev.length) {
        next.length = lengthWeeks;
      }
      return next;
    });
  }, [lengthWeeks]);

  const addWeek = () => {
    setWeeks((prev) => [...prev, []]);
    setLengthWeeks((prev) => prev + 1);
  };

  const removeLastWeek = () => {
    setWeeks((prev) => {
      if (prev.length <= 1) return prev;
      const next = [...prev];
      next.pop();
      return next;
    });
    setLengthWeeks((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const templateOptions = templates.map((t) => ({
    id: t.id,
    label: t.name,
  }));

  const handleWeekChange = (weekIndex, newValue) => {
    setWeeks((prev) => {
      const next = [...prev];
      next[weekIndex] = newValue.map((v) => v.id);
      return next;
    });
  };

  const getWeekValue = (ids = []) =>
    ids.map((id) => templateOptions.find((t) => t.id === id)).filter(Boolean);

  const handleSave = () => {
    onSave({
      name,
      lengthWeeks: weeks.length,
      weeks,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialProgram ? "Edit Program" : "New Program"}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <TextField
            label="Program Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Length (weeks)"
            type="number"
            value={lengthWeeks}
            onChange={(e) => setLengthWeeks(Number(e.target.value) || 1)}
            size="small"
            sx={{ width: 180 }}
            inputProps={{ min: 1 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {weeks.map((weekTemplates, idx) => (
            <Box key={idx}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Week {idx + 1}
              </Typography>
              <Autocomplete
                multiple
                options={templateOptions}
                value={getWeekValue(weekTemplates)}
                getOptionLabel={(option) => option.label || ""}
                onChange={(e, newValue) => handleWeekChange(idx, newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.label}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Templates (order is preserved)"
                    size="small"
                  />
                )}
              />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          <Button variant="outlined" size="small" onClick={addWeek}>
            Add Week
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            disabled={weeks.length <= 1}
            onClick={removeLastWeek}
          >
            Remove Last Week
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TemplatesList = ({
  templates,
  activeTemplateId,
  setActiveTemplateId,
  onCreateNew,
  isMobile,
  setDrawerOpen,
  theme,
  getTemplateLabel,
  programOptions,
  selectedProgramId,
  onProgramChange,
  onNewProgram,
  onEditProgram,
  onDeleteProgram,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = React.useRef();

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const label = getTemplateLabel
        ? getTemplateLabel(template)
        : template.name;
      return label.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [templates, searchQuery, getTemplateLabel]);

  useEffect(() => {
    // If user is creating a new template, don't auto-switch selection
    if (activeTemplateId === "new") return;

    if (filteredTemplates.length > 0) {
      if (
        !activeTemplateId ||
        !filteredTemplates.find((t) => t.id === activeTemplateId)
      ) {
        setActiveTemplateId(filteredTemplates[0].id);
      }
    } else if (templates.length > 0 && searchQuery === "") {
      setActiveTemplateId(templates[0].id);
    } else {
      setActiveTemplateId(null);
    }
  }, [filteredTemplates, templates, searchQuery, activeTemplateId]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2 }}>
        <Button onClick={onNewProgram} fullWidth sx={{ mb: 1 }}>
          New Program
        </Button>
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel>Program</InputLabel>
          <Select
            value={selectedProgramId}
            label="Program"
            onChange={(e) => onProgramChange(e.target.value)}
          >
            {programOptions.map((opt) => (
              <MenuItem value={opt.id} key={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {selectedProgramId !== "all" &&
            selectedProgramId !== "none" &&
            selectedProgramId && (
              <>
                <Button variant="outlined" size="small" onClick={onEditProgram}>
                  Edit Program
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={onDeleteProgram}
                >
                  Delete Program
                </Button>
              </>
            )}
        </Box>
        <Button onClick={onCreateNew} fullWidth sx={{ mb: 2 }}>
          New Template
        </Button>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {filteredTemplates.length ? (
          filteredTemplates.map((template) => (
            <Paper
              sx={{
                mx: 2,
                mb: 2,
                p: 2,
                backgroundColor:
                  activeTemplateId === template.id
                    ? theme.palette.primary.main
                    : "white",
                color: activeTemplateId === template.id ? "white" : "inherit",
                cursor: "pointer",
                overflow: "hidden",
              }}
              key={template.id}
              onClick={() => {
                setActiveTemplateId(template.id);
                if (isMobile) setDrawerOpen(false);
              }}
            >
              <Typography
                color={activeTemplateId === template.id ? "white" : "inherit"}
                sx={{
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "& .description": {
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <b>
                  {getTemplateLabel
                    ? getTemplateLabel(template)
                    : template.name}
                </b>
                {template.description && (
                  <>
                    <br />
                    <span className="description">{template.description}</span>
                  </>
                )}
              </Typography>
            </Paper>
          ))
        ) : searchQuery ? (
          <Paper sx={{ m: 2, p: 2 }}>
            <Typography sx={{ textAlign: "center", color: "text.secondary" }}>
              No templates found matching "{searchQuery}"
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ m: 2, p: 2 }}>
            <Box>
              <Typography sx={{ textAlign: "center", color: "red" }}>
                <b>No workout templates!</b>
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const Templates = () => {
  const {
    getWorkoutTemplates,
    createWorkoutTemplate,
    updateWorkoutTemplate,
    deleteWorkoutTemplate,
    getPrograms,
    createProgram,
    updateProgram,
    deleteProgram,
  } = useApi();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("all");
  const [programModalOpen, setProgramModalOpen] = useState(false);
  const [programEditing, setProgramEditing] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isMobile]);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const fetchTemplates = async (templateIdToSelect) => {
    setLoading(true);
    const response = await getWorkoutTemplates();
    const sortedTemplates =
      response?.sort((a, b) => a.name.localeCompare(b.name)) || [];
    setTemplates(sortedTemplates);
    setActiveTemplateId((prev) => {
      if (templateIdToSelect) return templateIdToSelect;
      if (prev && sortedTemplates.find((t) => t.id === prev)) return prev;
      return sortedTemplates[0]?.id || null;
    });
    setLoading(false);
  };

  const fetchPrograms = async () => {
    const response = await getPrograms();
    const sorted = response?.sort((a, b) => a.name.localeCompare(b.name)) || [];
    setPrograms(sorted);
    // If selected program was deleted, reset to all
    if (
      selectedProgramId !== "all" &&
      selectedProgramId !== "none" &&
      !sorted.find((p) => p.id === selectedProgramId)
    ) {
      setSelectedProgramId("all");
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchPrograms();
  }, []);

  const handleCreateNew = () => {
    setActiveTemplateId("new");
  };

  const handleSaveTemplate = async (workoutPayload) => {
    if (activeTemplateId && activeTemplateId !== "new") {
      await updateWorkoutTemplate(activeTemplateId, workoutPayload);
      await fetchTemplates(activeTemplateId);
      return;
    }

    const response = await createWorkoutTemplate({
      coachId: user?.uid,
      workoutInfo: workoutPayload,
    });
    const newTemplateId = response?.template?.id;
    await fetchTemplates(newTemplateId);
  };

  const handleDeleteTemplate = async () => {
    if (!activeTemplateId || activeTemplateId === "new") return;
    await deleteWorkoutTemplate(activeTemplateId);
    const remainingTemplateId =
      templates.find((t) => t.id !== activeTemplateId)?.id || null;
    await fetchTemplates(remainingTemplateId);
  };

  const programOptions = useMemo(
    () => [
      { id: "all", label: "All Templates" },
      { id: "none", label: "No Program" },
      ...programs.map((p) => ({ id: p.id, label: p.name })),
    ],
    [programs]
  );

  const templatesUsedInPrograms = useMemo(() => {
    const used = new Set();
    programs.forEach((p) => {
      (p.weeks || []).forEach((week) => week?.forEach((id) => used.add(id)));
    });
    return used;
  }, [programs]);

  const programTemplateOrder = useMemo(() => {
    if (
      selectedProgramId === "all" ||
      selectedProgramId === "none" ||
      !programs.length
    ) {
      return [];
    }
    const program = programs.find((p) => p.id === selectedProgramId);
    if (!program) return [];
    const orderedIds = [];
    (program.weeks || []).forEach((weekIds = [], weekIdx) => {
      weekIds.forEach((id, idx) => {
        orderedIds.push({ id, week: weekIdx + 1, order: idx });
      });
    });
    return orderedIds;
  }, [programs, selectedProgramId]);

  const templatesToShow = useMemo(() => {
    if (selectedProgramId === "none") {
      return templates.filter((t) => !templatesUsedInPrograms.has(t.id));
    }
    if (selectedProgramId === "all" || !programTemplateOrder.length) {
      return templates;
    }

    const orderMap = new Map(programTemplateOrder.map((o, idx) => [o.id, idx]));
    const enriched = programTemplateOrder
      .map((o) => {
        const template = templates.find((t) => t.id === o.id);
        if (!template) return null;
        return { ...template, _week: o.week, _order: o.order };
      })
      .filter(Boolean);
    return enriched.sort((a, b) => {
      if (a._week === b._week) return a._order - b._order;
      return a._week - b._week;
    });
  }, [
    templates,
    templatesUsedInPrograms,
    selectedProgramId,
    programTemplateOrder,
  ]);

  // When program selection changes, reset active template to first in list (unless creating new)
  useEffect(() => {
    if (activeTemplateId === "new") return;
    const currentExists = templatesToShow?.some(
      (t) => t.id === activeTemplateId
    );
    if (currentExists) return;
    const first = templatesToShow?.[0]?.id || null;
    setActiveTemplateId(first);
  }, [selectedProgramId, templatesToShow, activeTemplateId]);

  const getTemplateLabel = useCallback(
    (template) => {
      if (
        selectedProgramId === "all" ||
        selectedProgramId === "none" ||
        template._week === undefined
      ) {
        return template.name;
      }
      return `Week ${template._week}: ${template.name}`;
    },
    [selectedProgramId]
  );

  const activeTemplate =
    activeTemplateId === "new"
      ? {
          name: "",
          description: "",
          exercises: [],
          currentWeek: 1,
          programLength: 12,
        }
      : templates.find((t) => t.id === activeTemplateId);

  const handleProgramModalClose = () => {
    setProgramModalOpen(false);
    setProgramEditing(null);
  };

  const handleProgramSave = async (programPayload) => {
    if (programEditing) {
      await updateProgram(programEditing.id, programPayload);
    } else {
      await createProgram({ coachId: user?.uid, ...programPayload });
    }
    await fetchPrograms();
    handleProgramModalClose();
  };

  const handleProgramDelete = async () => {
    if (
      selectedProgramId === "all" ||
      selectedProgramId === "none" ||
      !selectedProgramId
    )
      return;
    await deleteProgram(selectedProgramId);
    setSelectedProgramId("all");
    await fetchPrograms();
  };

  if (loading) {
    return (
      <Container sx={{ mt: 10 }}>
        <Loading />
      </Container>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)" }}>
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            top: "64px",
            height: "calc(100% - 64px)",
          },
        }}
      >
        <TemplatesList
          templates={templatesToShow}
          activeTemplateId={activeTemplateId}
          setActiveTemplateId={setActiveTemplateId}
          onCreateNew={handleCreateNew}
          isMobile={isMobile}
          setDrawerOpen={setDrawerOpen}
          theme={theme}
          getTemplateLabel={getTemplateLabel}
          programOptions={programOptions}
          selectedProgramId={selectedProgramId}
          onProgramChange={(id) => setSelectedProgramId(id)}
          onNewProgram={() => {
            setProgramEditing(null);
            setProgramModalOpen(true);
          }}
          onEditProgram={() => {
            const program = programs.find((p) => p.id === selectedProgramId);
            setProgramEditing(program);
            setProgramModalOpen(true);
          }}
          onDeleteProgram={handleProgramDelete}
        />
      </Drawer>

      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: "auto",
          ml: drawerOpen ? "400px" : 0,
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <IconButton
              onClick={toggleDrawer}
              sx={{
                display: { md: "block" },
                backgroundColor: "background.paper",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                p: 0,
              }}
            >
              <MenuIcon />
            </IconButton>
            {activeTemplate && activeTemplate.name && (
              <Typography variant="h6" sx={{ mb: 1 }}>
                {activeTemplate.name}
              </Typography>
            )}
          </Box>
          {activeTemplate ? (
            <AddWorkoutDetails
              mode="template"
              workout={activeTemplate}
              onSave={handleSaveTemplate}
              onDeleteTemplate={handleDeleteTemplate}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select or create a template to begin editing
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <ProgramModal
        open={programModalOpen}
        onClose={handleProgramModalClose}
        templates={templates}
        initialProgram={programEditing}
        onSave={handleProgramSave}
      />
    </Box>
  );
};

export default Templates;
